import { Button } from "@/components/ui/baseUi/button"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { cn } from "@/modules/lib/utils"
import { type AddressAutofillSuggestionResponse } from "@mapbox/search-js-core"
import { useAddressAutofillCore } from "@mapbox/search-js-react"
import { type BoxFormikType, convertBoxFormFormikToInputDto, getValidationSchema } from "@modules/box/boxFormikType"
import { BoxSecondaryInfoForm } from "@modules/box/components/boxSecondaryInfo"
import { BoxMainInfoForm } from "@modules/box/components/boxUpdateForm/mainBoxInfoForm"
import { BoxDetailsEntity } from "@modules/box/entities/boxEntity"
import { useUpdateBox } from "@modules/box/hooks/useBoxActions"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { Config } from "@modules/config"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage } from "@modules/formik/formikUtils"
import { minScreenWidth } from "@modules/ui/uiConstants"
import { LoggerType } from "@modules/utils/loggerUtils"
import { useFormik } from "formik"
import { LoaderCircle } from "lucide-react"
import { useRef, useState } from "react"

type BoxFormProps = {
  box: BoxDetailsEntity
  onSave?: () => void
  className?: string
} & React.ComponentProps<"div">

export function BoxForm({ box, onSave, className, ...props }: BoxFormProps) {
  const [isImageUploading, setIsImageUploading] = useState(false)
  const [loading, setLoading] = useState(false)

  const containerRef = useRef<HTMLFormElement>(null)

  const canEditGymInformation = useBoxStaffPermission(BoxStaffPermissions.EDIT_GYM_INFORMATION)

  const { updateBox: mutateUpdateBox } = useUpdateBox()
  const autofill = useAddressAutofillCore({
    accessToken: Config.MAPBOX_API_KEY,
  })

  const formik = useFormik<BoxFormikType>({
    initialValues: box.getBoxFormik(),
    validationSchema: getValidationSchema(),
    onSubmit: async (values) => {
      if (!canEditGymInformation) {
        return
      }
      setLoading(true)

      const updateBox = () => {
        mutateUpdateBox(
          { boxId: box.id, box: convertBoxFormFormikToInputDto(values) },
          {
            onSuccess: () => {
              onSave?.()
              toast.success(word("box.form.update.success"), {
                classNames: successToastClassName(),
              })
              setLoading(false)
            },
            onError: (error) => {
              console.error(LoggerType.Box + "update", error)
              toast.error(word("global.error.label"), {
                description: error.message,
                classNames: errorToastClassName(),
              })
              setLoading(false)
            },
          },
        )
      }

      const res: AddressAutofillSuggestionResponse = await autofill.suggest(
        values.address + " " + values.postalCode + " " + values.city,
        {
          sessionToken: undefined,
        },
      )

      if (
        res.suggestions.length > 0 &&
        res.suggestions.find(
          (suggestion) =>
            suggestion.address_line1?.toLowerCase() === values.address.toLowerCase() &&
            suggestion.address_level2?.toLowerCase() === values.city.toLowerCase() &&
            (suggestion.postcode?.toLowerCase() === values.postalCode.toLowerCase() ||
              suggestion.match_code.postcode.toString() === "not_applicable") &&
            (suggestion.country_code?.toLowerCase() === values.country.toLowerCase() ||
              ("iso_3166_2" in suggestion.metadata &&
                typeof suggestion.metadata.iso_3166_2 === "string" &&
                suggestion.metadata.iso_3166_2?.toLowerCase() === values.country.toLowerCase())),
        )
      ) {
        updateBox()
      } else {
        toast.error(word("global.error.label"), {
          description: word("form.address.error.select"),
          classNames: errorToastClassName(),
        })
        formik.setFieldError("address", word("form.address.error.invalid"))
        setLoading(false)
      }
    },
  })

  return (
    <div className={cn("flex justify-center", className)} style={{ minWidth: minScreenWidth }} {...props}>
      <form onSubmit={formik.handleSubmit} className="flex w-full flex-col" ref={containerRef}>
        <div className="mb-4 flex flex-col justify-center gap-6 xl:flex-row">
          <BoxMainInfoForm getFormErrorMessage={(input) => getFormErrorMessage(input, formik)} formik={formik} />

          <BoxSecondaryInfoForm formik={formik} setIsImageUploading={setIsImageUploading} />
        </div>

        <div className="flex flex-row-reverse justify-between">
          <Button
            variant="primary"
            size="lg"
            onClick={() => {
              const errorElements = containerRef.current?.querySelectorAll('[aria-invalid="true"]')
              if (errorElements && errorElements.length > 0) {
                const y = (errorElements[0] as HTMLElement).getBoundingClientRect().top + window.pageYOffset - 30
                window.scrollTo({ top: y, behavior: "smooth" })
              }
              void formik.submitForm()
            }}
            isLoading={loading}
            disabled={!canEditGymInformation || isImageUploading || loading}
          >
            {loading && <LoaderCircle className="animate-spin" />}
            {word("global.save")}
          </Button>
        </div>
      </form>
    </div>
  )
}
